import type { Anomaly, GameData, PropertyType } from '../game/types';

/**
 * Compares two property arrays for equality, count-sensitive and order-independent.
 * [Lethal, Lethal] is different from [Lethal, Memetic]
 * [Lethal, Memetic] is the same as [Memetic, Lethal]
 */
function propertiesMatch(properties1: PropertyType[], properties2: PropertyType[]): boolean {
  if (properties1.length !== properties2.length) {
    return false;
  }

  // Create sorted copies to compare
  const sorted1 = [...properties1].sort();
  const sorted2 = [...properties2].sort();

  return sorted1.every((prop, index) => prop === sorted2[index]);
}

/**
 * Gets the display name for an anomaly based on how many properties are known.
 * - 1 property known: Look up in one_property_names.yaml
 * - 2 properties known: Look up in two_properties_names.yaml
 * - 3 properties known: Return the full anomaly name
 */
export function getAnomalyDisplayName(anomaly: Anomaly, gameData: GameData): string {
  const knownCount = anomaly.knownProperties.length;

  if (knownCount === 0) {
    // Fallback: shouldn't happen in normal gameplay, but handle gracefully
    return 'Unknown Anomaly';
  }

  if (knownCount === 1) {
    // Look up in one_property_names.yaml
    const entry = gameData.onePropertyNames.find(entry => 
      entry.properties.length === 1 && 
      entry.properties[0] === anomaly.knownProperties[0]
    );

    if (entry) {
      return entry.name;
    }

    // Fallback if no match found
    return `${anomaly.knownProperties[0]}??`;
  }

  if (knownCount === 2) {
    // Look up in two_properties_names.yaml
    // Match must be count-sensitive and order-independent
    const entry = gameData.twoPropertyNames.find(entry => 
      propertiesMatch(entry.properties, anomaly.knownProperties)
    );

    if (entry) {
      return entry.name;
    }

    // Fallback if no match found
    const prop1 = anomaly.knownProperties[0];
    const prop2 = anomaly.knownProperties[1];
    return `${prop1}/${prop2}?`;
  }

  // 3 properties known: return full name
  return anomaly.data.name;
}

/**
 * Gets the description for an anomaly based on how many properties are known.
 * - 1 property known: Look up in one_property_names.yaml
 * - 2 properties known: Look up in two_properties_names.yaml
 * - 3 properties known: Return the full anomaly description
 */
export function getAnomalyDescription(anomaly: Anomaly, gameData: GameData): string {
  const knownCount = anomaly.knownProperties.length;

  if (knownCount === 0) {
    // Fallback: shouldn't happen in normal gameplay, but handle gracefully
    return 'Unknown anomaly. No information available.';
  }

  if (knownCount === 1) {
    // Look up in one_property_names.yaml
    const entry = gameData.onePropertyNames.find(entry => 
      entry.properties.length === 1 && 
      entry.properties[0] === anomaly.knownProperties[0]
    );

    if (entry) {
      return entry.description;
    }

    // Fallback if no match found
    return `Anomaly with ${anomaly.knownProperties[0]} property.`;
  }

  if (knownCount === 2) {
    // Look up in two_properties_names.yaml
    // Match must be count-sensitive and order-independent
    const entry = gameData.twoPropertyNames.find(entry => 
      propertiesMatch(entry.properties, anomaly.knownProperties)
    );

    if (entry) {
      return entry.description;
    }

    // Fallback if no match found
    return `Anomaly with ${anomaly.knownProperties[0]} and ${anomaly.knownProperties[1]} properties.`;
  }

  // 3 properties known: return full description
  return anomaly.data.description;
}
